/**
 * Typed endpoint wrappers for the /admin/* surface.
 *
 * Same contract as `endpoints.ts`: keep these thin. Validate nothing, shape
 * nothing, just call `api.*` and return the typed response. Business logic
 * belongs in the React Query hooks in `hooks/useAdmin.ts`.
 *
 * WHY THIS IS A SEPARATE FILE rather than more namespaces in endpoints.ts:
 * every function here is behind a role gate that the API enforces and the SPA
 * only mirrors. Keeping them apart makes it obvious at a glance whether a call
 * site is customer-facing or staff-facing, and means a customer bundle that
 * accidentally imports one is visible in a diff rather than buried in a
 * 200-line module.
 *
 * THE API IS THE AUTHORITY ON ROLE. Nothing in this file checks a role, and
 * nothing in the SPA should be trusted to. `RequireAdminRole` hides the
 * navigation; the server re-checks the JWT claim on every single request and
 * returns 403 regardless of what the client believed.
 */
import { api } from './client';
import type {
  AdminAdjustMatchCreditBody,
  AdminAdjustMatchCreditResult,
  AdminApplyQuarantineBody,
  AdminAuditPage,
  AdminClearQuarantineBody,
  AdminFixtureQuarantineList,
  AdminMarketVisibility,
  AdminOddsEngineAliasGaps,
  AdminOddsEngineCache,
  AdminOddsEngineHealth,
  AdminOddsEngineProviders,
  AdminOddsEngineSettlementCoverage,
  AdminOpsAlertsReport,
  AdminParams,
  AdminRejectResettlementBody,
  AdminRejectResettlementResult,
  AdminReportsSummary,
  AdminResettleBody,
  AdminResettleResult,
  AdminResolveReferralBody,
  AdminResolveReferralResult,
  AdminSetKycBody,
  AdminSetKycResult,
  AdminSetRoleBody,
  AdminSetRoleResult,
  AdminSetStatusBody,
  AdminSetStatusResult,
  AdminSettleBetBody,
  AdminSettleBetResult,
  AdminUnlockSavingsBody,
  AdminUnlockSavingsResult,
  AdminUserDetail,
  AdminUserListItem,
  BetResettlementRequest,
  BetResettlementStatus,
} from '@/types/api';

/** Build a query string, omitting empty/undefined values. Returns '' or '?a=b'. */
function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

export const adminOpsApi = {
  /**
   * Every standing detector, evaluated on demand. Read-only and safe to poll.
   *
   * This is the answer to "a detector nobody receives is not a detector" —
   * the scheduler's email alerts are inert without POSTMARK_API_TOKEN, so the
   * conditions are read here instead of waited for.
   */
  getAlerts: () => api.get<AdminOpsAlertsReport>('/admin/ops-alerts'),

  /** Which sports can actually settle, plus the catalogue posture marker. */
  getSettlementCoverage: () =>
    api.get<AdminOddsEngineSettlementCoverage>('/admin/odds-engine/settlement-coverage'),

  /*
    The four odds-engine diagnostics below are ANY_ADMIN and READ-ONLY, and
    none of them is on the money path. `validateMarketForPlacement` consults
    none of these — it re-derives every acceptance condition from the locked
    quote row. So a stale or unavailable diagnostic here cannot strike a bet;
    the worst it can do is mislead an operator, which is why each screen
    renders the fetch timestamp rather than a render-time clock.
  */

  /** What each adapter DECLARES it covers — registry config, not a live probe. */
  getOddsProviders: () => api.get<AdminOddsEngineProviders>('/admin/odds-engine/providers'),

  /** The live per-adapter probe. Distinct from `getOddsProviders`: this one can fail. */
  getOddsHealth: () => api.get<AdminOddsEngineHealth>('/admin/odds-engine/health'),

  /** TTL cache counters for the quote and result fan-outs. */
  getOddsCache: () => api.get<AdminOddsEngineCache>('/admin/odds-engine/cache'),

  /**
   * Where providers disagree about identity, plus a structural self-check of
   * the market alias table. `marketGaps` is SUSPICION, not error — a provider
   * may genuinely be the only one offering a niche market.
   */
  getAliasGaps: () => api.get<AdminOddsEngineAliasGaps>('/admin/odds-engine/alias-gaps'),

  /**
   * WRITE (owner|admin — deliberately NOT support). Drops both TTL caches.
   *
   * NO BODY AND NO REASON FIELD: the contract declares `requestBody?: never`
   * and a 204. That is why the console confirms this one through the plain
   * Modal rather than `ConfirmAction` — collecting a mandatory reason the
   * server has nowhere to store would tell the operator an account of why was
   * recorded when none was.
   *
   * The cost is real: the next fan-out re-fetches from every provider and
   * spends rate-limit budget, so this is not a refresh button.
   */
  invalidateOddsCache: () => api.post<void>('/admin/odds-engine/cache/invalidate'),
};

export const adminParamsApi = {
  get: () => api.get<AdminParams>('/admin/params'),

  /**
   * Write the single config row.
   *
   * `expectedVersion` is REQUIRED by the contract, not optional — it is the
   * token that lets the server detect that this body was composed from a
   * console loaded before someone else's write landed. Omitting it would
   * silently revert a colleague's commercial decision with a 200. A mismatch
   * returns 409 `stale_params_version` carrying the current snapshot, so the
   * caller can re-render without a second round trip.
   */
  update: (body: Partial<AdminParams> & { expectedVersion: number; note?: string }) =>
    api.put<AdminParams>('/admin/params', body),

  auditLog: (params: { limit?: number; cursor?: string } = {}) =>
    api.get<AdminAuditPage>(`/admin/audit-log${qs(params)}`),
};

export interface AdminUsersQuery {
  limit?: number;
  cursor?: string;
  q?: string;
  /** 'customer' matches role IS NULL; otherwise the literal AdminRole. */
  role?: 'customer' | 'owner' | 'admin' | 'compliance' | 'support' | 'readonly';
  kycStatus?: 'unverified' | 'pending' | 'verified' | 'rejected';
  status?: 'active' | 'suspended' | 'closed';
}

export const adminUsersApi = {
  list: (params: AdminUsersQuery = {}) =>
    api.get<{ items: AdminUserListItem[]; nextCursor: string | null }>(
      `/admin/users/all${qs(params as Record<string, string | number | undefined>)}`,
    ),

  detail: (userId: string) => api.get<AdminUserDetail>(`/admin/users/${userId}/detail`),

  /**
   * WRITE (owner|admin). The 200 body carries the server's own `note` telling
   * the operator the target must re-authenticate — rendered as returned rather
   * than restated here, so the reminder cannot drift from what the API means.
   */
  setRole: (userId: string, body: AdminSetRoleBody) =>
    api.put<AdminSetRoleResult>(`/admin/users/${userId}/role`, body),

  /** Staff override after offline verification. Audit-logged against the actor. */
  setKyc: (userId: string, body: AdminSetKycBody) =>
    api.put<AdminSetKycResult>(`/admin/users/${userId}/kyc`, body),

  /** Suspend/close/reactivate. `revokedSessions` is non-zero only when locking out. */
  setStatus: (userId: string, body: AdminSetStatusBody) =>
    api.put<AdminSetStatusResult>(`/admin/users/${userId}/status`, body),

  /**
   * COMPLIANCE_OR_HIGHER. Releases locked Savings into Play Balance.
   *
   * `Idempotency-Key` is REQUIRED by the contract, and this is the one call in
   * the console where that matters most: a replay without the key is not a
   * duplicate no-op, it is a SECOND FIFO tranche released. The key is generated
   * once per confirmed action by the caller, not per attempt, so a retry after
   * a network failure resolves to the same release.
   */
  unlockSavings: (userId: string, body: AdminUnlockSavingsBody, idempotencyKey: string) =>
    api.post<AdminUnlockSavingsResult>(`/admin/users/${userId}/savings/unlock`, body, {
      'Idempotency-Key': idempotencyKey,
    }),
};

export const adminMatchCreditApi = {
  /** Signed, non-zero. 422 `insufficient_match_credit` when a debit exceeds the balance. */
  adjust: (body: AdminAdjustMatchCreditBody) =>
    api.post<AdminAdjustMatchCreditResult>('/admin/match-credit/adjust', body),
};

export const adminReferralsApi = {
  /**
   * Resolve a fraud-HELD attribution. 409 `not_held` means someone else already
   * resolved it — a distinct answer from a failure, and presented as one.
   */
  resolve: (attributionId: string, body: AdminResolveReferralBody) =>
    api.put<AdminResolveReferralResult>(
      `/admin/referral-attributions/${attributionId}/resolve`,
      body,
    ),
};

export const adminResettlementApi = {
  /**
   * The correction queue. ANY_ADMIN and read-only — deliberately wider than the
   * two writes below, so support staff can answer "why does my bet say won when
   * the site says lost" without being able to act on it.
   */
  list: (params: { status?: BetResettlementStatus; limit?: number } = {}) =>
    api.get<{ items: BetResettlementRequest[] }>(`/admin/bet-resettlement-requests${qs(params)}`),

  /**
   * OWNER_ONLY. Reverses the recorded settlement from the LEDGER and applies a
   * replacement, in one transaction.
   *
   * Every field of the body except `reason` is carried from the proposal row.
   * `expectedSettlementVersion` especially: it is the optimistic-concurrency
   * guard, and a hand-typed one defeats the only thing standing between an
   * operator and a second correction stacked on a first they never saw. A
   * mismatch is 409 `version_mismatch`.
   */
  resettle: (betId: string, body: AdminResettleBody) =>
    api.post<AdminResettleResult>(`/admin/bets/${betId}/resettle`, body),

  /**
   * OWNER_ONLY, same as applying — "the provider is wrong and the original
   * stands" is an equally accountable ruling, and must not be a queue-clearing
   * click by support staff. 409 `not_pending` if someone got there first.
   */
  reject: (id: string, body: AdminRejectResettlementBody) =>
    api.post<AdminRejectResettlementResult>(
      `/admin/bet-resettlement-requests/${id}/reject`,
      body,
    ),
};

export const adminBetsApi = {
  /**
   * Settle a PENDING bet. ANY_ADMIN per the contract, and distinct from
   * `resettle` above in the one way that matters: nothing is being reversed,
   * because nothing has moved yet. Idempotent on bet status — settling an
   * already-settled bet returns its existing state with 200 and emits nothing.
   */
  settle: (betId: string, body: AdminSettleBetBody) =>
    api.post<AdminSettleBetResult>(`/admin/bets/${betId}/settle`, body),
};

export const adminReportsApi = {
  summary: () => api.get<AdminReportsSummary>('/admin/reports/summary'),
};

export const adminQuarantineApi = {
  /**
   * Fixtures structurally withheld from the board. ANY_ADMIN, read-only.
   *
   * The list carries BOTH lifecycle states on purpose. `pending_refresh` — an
   * operator asked for the quarantine to be cleared, and the fixture is still
   * unplaceable until its OWN catalogue transaction rewrites it — appears on no
   * other surface in the console. Filtering it out here would make the one
   * state nobody can otherwise see the one state nobody can see.
   */
  list: () =>
    api.get<AdminFixtureQuarantineList>('/admin/odds-engine/fixture-quarantines'),

  /**
   * WRITE. Withhold a fixture and suspend its markets. 409
   * `fixture_already_quarantined` when one is already active — which is an
   * answer, not a failure: someone else got there first.
   *
   * Typed `void` because the contract declares no response content for the 201.
   * Hand-writing the shape the handler happens to send today is exactly the
   * drift `types/api.ts` exists to prevent; the screen re-reads the list.
   */
  apply: (body: AdminApplyQuarantineBody) =>
    api.post<void>('/admin/odds-engine/fixture-quarantines', body),

  /**
   * WRITE. Requests a clear — it does NOT reopen the fixture.
   *
   * The reason travels in the DELETE BODY because the contract requires it: it
   * is the audit record for an operator's decision to let a withheld fixture
   * back toward the board. 404 when no quarantine is held, 409
   * `fixture_quarantine_clear_pending` when one is already waiting on a refresh.
   * `void` for the same reason as `apply`.
   */
  clear: (fixtureId: string, body: AdminClearQuarantineBody) =>
    api.delete<void>(`/admin/odds-engine/fixture-quarantines/${fixtureId}`, body),
};

export const adminMarketsApi = {
  /**
   * PURE READ. The write half is `hiddenMarkets` on PUT /admin/params, which
   * is WRITE-gated — so support staff can see the grid without changing it.
   */
  visibility: () => api.get<AdminMarketVisibility>('/admin/markets/visibility'),
};
